/**
 * Single Option Variant Picker
 * ============================
 * Extends VariantPickerBase for products with a single variant option.
 * Suitable for products like jerseys (select number) or simple size selection.
 * Rule: each selection emits one full theme variant; a dropdown placeholder emits null.
 * Missing variant data and programmatic resets emit none.
 *
 * @class SingleOptionVariantPicker
 * @extends VariantPickerBase
 *
 * Features:
 * - Multiple display types (searchable-grid, grid, dropdown, buttons)
 * - Single option selection (simpler than multi-option)
 * - URL variant preselection
 * - Inventory tracking support
 * - Accessible and responsive
 *
 * Usage:
 * <div data-single-option-picker
 *      data-picker-type="searchable-grid"
 *      data-option-name="Number"
 *      data-product-id="{{ product.id }}"
 *      data-total-variants="{{ product.variants.size }}">
 *   <!-- picker content -->
 * </div>
 */

class SingleOptionVariantPicker extends VariantPickerBase {
  /**
   * Creates a new SingleOptionVariantPicker instance
   * @param {HTMLElement} element - The container element for the variant picker
   */
  constructor(element) {
    super(element);

    // Picker configuration
    this.pickerType = this.container.dataset.pickerType || 'grid';
    this.optionName = this.container.dataset.optionName || 'Option';

    // Single-option specific elements
    this.variantButtons = this.container.querySelectorAll('[data-variant-button]');
    this.variantDropdown = this.container.querySelector('[data-variant-dropdown]');
    this.selectedDisplay = this.container.querySelector('[data-selected-display]');
    this.noResultsMessage = this.container.querySelector('[data-no-results]');
    this.inventoryWarning = this.container.querySelector('[data-inventory-warning]');

    // State
    this.selectedValue = null;

    // The full product JSON is a sibling of .js-product inside the Shopify section.
    // product-json-* contains 3D models, not variants. Cache each JSON script once.
    this.productSection = this.container.closest('.js-product') || this.productSection;
    const productRoot = this.container.closest('.shopify-section') || this.productSection || document;
    const productData = this._readJSON(productRoot.querySelector('[data-product-json]'));
    this.variants = Array.isArray(productData?.variants) ? productData.variants : [];
    this.formatted = this._readJSON(this.container.querySelector('[data-variant-data]'))?.formatted || {};

    // Initialize the picker
    this.init();
  }

  /**
   * Initializes the picker based on picker type
   */
  init() {
    switch (this.pickerType) {
      case 'searchable-grid':
        this.initSearchableGrid();
        break;
      case 'grid':
        this.initGrid();
        break;
      case 'dropdown':
        this.initDropdown();
        break;
      case 'buttons':
        this.initButtons();
        break;
      case 'type-buttons':
        this.initButtons();
        break;
      default:
        this.initGrid();
    }

    // Combined listings pre-select via server-side rendering — just set the variant ID
    this.isCombinedListing = !!this.container.querySelector('[data-combined-listing]');
    if (this.isCombinedListing) {
      const currentBtn = this.container.querySelector('[data-current-product]');
      if (currentBtn) {
        this.selectedVariantId = currentBtn.dataset.variantId;
        this.selectedValue = currentBtn.dataset.variantValue;
        this._updateVariantIdInput(currentBtn.dataset.variantId);
        this._enableAddToCartButton();
        this.updateSelectedDisplay(currentBtn.dataset.variantValue);
        this._updateThemeVariantSelection(currentBtn.dataset.variantId);
      }
    } else {
      // Check for pre-selected variant from URL
      this.checkPreselectedVariant();
    }

    // Initialize base class functionality
    this._baseInit();

    // Dispatch picker-specific ready event
    this.container.dispatchEvent(new CustomEvent('single-option-picker:ready', {
      bubbles: true,
      detail: {
        pickerType: this.pickerType,
        optionName: this.optionName,
        totalVariants: this.totalVariants
      }
    }));
  }

  // ============================================================
  // PICKER TYPE INITIALIZATION
  // ============================================================

  /**
   * Initializes searchable grid picker with search functionality
   * @private
   */
  initSearchableGrid() {
    // Setup search functionality
    this.searchInputs.forEach(input => {
      input.addEventListener('input', this.handleSearch.bind(this));
    });

    // Focus search on load for better UX
    const primarySearch = this.container.querySelector('[data-search-input]');
    if (primarySearch) {
      setTimeout(() => {
        primarySearch.focus();
      }, 300);
    }

    // Setup button clicks
    this.initGrid();
  }

  /**
   * Initializes grid picker with button clicks
   * @private
   */
  initGrid() {
    this.variantButtons.forEach(button => {
      button.addEventListener('click', this.handleVariantSelect.bind(this));
    });
  }

  /**
   * Initializes dropdown picker
   * @private
   */
  initDropdown() {
    if (this.variantDropdown) {
      this.variantDropdown.addEventListener('change', this.handleDropdownChange.bind(this));
    }
  }

  /**
   * Initializes button picker (same as grid)
   * @private
   */
  initButtons() {
    this.initGrid();
  }

  // ============================================================
  // SEARCH HANDLING
  // ============================================================

  /**
   * Handles search input for filtering variant buttons
   * @param {Event} event - The input event
   */
  handleSearch(event) {
    this._handleSearch(event, this.variantButtons, this.noResultsMessage);
  }

  // ============================================================
  // SELECTION HANDLING
  // ============================================================

  /**
   * Handles variant button selection
   * @param {Event} event - The click event
   */
  handleVariantSelect(event) {
    const button = event.currentTarget;

    // Ignore disabled buttons
    if (button.disabled) return;

    // Combined listing: navigate to child product instead of partial swap
    const productUrl = button.dataset.productUrl;
    if (productUrl && productUrl !== window.location.pathname) {
      window.location.href = productUrl;
      return;
    }

    const variantId = button.dataset.variantId;
    const variantValue = button.dataset.variantValue;
    const originalValue = button.dataset.originalValue;

    // Update button states using parent method
    this._updateButtonStates(button, this.variantButtons);

    // Update state
    this.selectedVariantId = variantId;
    this.selectedValue = originalValue || variantValue;

    // Update hidden input using parent method
    this._updateVariantIdInput(variantId);

    // Update display
    this.updateSelectedDisplay(variantValue);

    // Enable form submission
    this._enableAddToCartButton();

    // Update URL
    this._updateURL(variantId);

    // Dispatch events
    this._dispatchVariantSelectedEvent(variantId, {
      variantValue: variantValue,
      optionName: this.optionName
    });
    this._updateThemeVariantSelection(variantId);

    // Scroll button into view on mobile
    this._scrollButtonIntoView(button);

    // Show inventory warning if applicable
    this.updateInventoryWarning(button);
  }

  /**
   * Handles dropdown variant selection
   * @param {Event} event - The change event
   */
  handleDropdownChange(event) {
    const variantId = event.target.value;

    if (!variantId) {
      this.selectedVariantId = null;
      this.selectedValue = null;
      this.updateSelectedDisplay(null);
      this._disableAddToCartButton(`Select a ${this.optionName.toLowerCase()} first`);
      this._clearURLVariant();
      this._dispatchThemeVariantChange(null);
      return;
    }

    const selectedOption = event.target.options[event.target.selectedIndex];
    // Extract variant value (remove price info if present)
    const variantValue = selectedOption.textContent.replace(/ - .*$/, '').trim();

    // Update state
    this.selectedVariantId = variantId;
    this.selectedValue = variantValue;

    // Update hidden input
    this._updateVariantIdInput(variantId);

    // Update display
    this.updateSelectedDisplay(variantValue);

    // Enable form submission
    this._enableAddToCartButton();

    // Update URL
    this._updateURL(variantId);

    // Dispatch events
    this._dispatchVariantSelectedEvent(variantId, {
      variantValue: variantValue,
      optionName: this.optionName
    });
    this._updateThemeVariantSelection(variantId);
  }

  /**
   * Reads optional JSON without interrupting selection or add-to-cart.
   * @param {HTMLElement|null} script - The JSON script element
   * @returns {Object|null}
   * @private
   */
  _readJSON(script) {
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * Updates SKU/price directly, as the other pickers do, and notifies the gallery.
   * @param {string|number} variantId - The selected variant ID
   * @private
   */
  _updateThemeVariantSelection(variantId) {
    const variant = this.variants.find(item => item?.id != null && String(item.id) === String(variantId));
    if (!variant) return;

    const sku = this.productSection?.querySelector('.product-sku__value');
    if (sku) {
      sku.textContent = variant.sku || '';
      sku.parentNode.hidden = !variant.sku;
    }

    // Use Liquid's formatted prices to preserve currency and superscript markup.
    const price = this.productSection?.querySelector('.product-info__price > .price');
    const formatted = this.formatted[variant.id];
    if (price && typeof formatted?.price === 'string') {
      const current = price.querySelector('.price__current');
      const was = price.querySelector('.price__was');
      const onSale = variant.compare_at_price > variant.price;
      if (current) current.innerHTML = formatted.price;
      if (was) was.innerHTML = onSale ? formatted.compareAtPrice || '' : '';
      price.classList.toggle('price--on-sale', onSale);
      price.classList.toggle('price--sold-out', !variant.available);
      const defaultPrice = price.querySelector('.price__default');
      const noVariant = price.querySelector('.price__no-variant');
      const from = price.querySelector('.price__from');
      if (defaultPrice) defaultPrice.hidden = false;
      if (noVariant) noVariant.hidden = true;
      if (from) from.hidden = true;
    }

    this._dispatchThemeVariantChange(variant);
  }

  // ============================================================
  // DISPLAY UPDATES
  // ============================================================

  /**
   * Updates the selected display text
   * @param {string|null} value - The selected variant value
   */
  updateSelectedDisplay(value) {
    if (!this.selectedDisplay) return;

    if (value) {
      if (this.pickerType === 'type-buttons') {
        this.selectedDisplay.innerHTML = `Selected: <strong>${value}</strong>`;
      } else {
        const totalLabel = this.totalVariants > 0 ? ` of ${this.totalVariants}` : '';
        this.selectedDisplay.innerHTML = `Selected: <strong>${this.optionName} ${value}${totalLabel}</strong>`;
      }
    } else {
      this.selectedDisplay.textContent = `No ${this.optionName.toLowerCase()} selected`;
    }
  }

  /**
   * Updates inventory warning based on selected variant
   * @param {HTMLElement} button - The selected button element
   * @private
   */
  updateInventoryWarning(button) {
    if (!this.inventoryWarning) return;

    const inventoryQty = parseInt(button.dataset.inventoryQuantity) || 0;
    const lowStockThreshold = parseInt(this.container.dataset.lowStockThreshold) || 5;

    if (inventoryQty > 0 && inventoryQty <= lowStockThreshold) {
      this.inventoryWarning.textContent = `Only ${inventoryQty} left in stock!`;
      this.inventoryWarning.hidden = false;
    } else {
      this.inventoryWarning.hidden = true;
    }
  }

  // ============================================================
  // COMBINED LISTING SUPPORT
  // ============================================================

  /**
   * Fetches a combined listing child product via Section Rendering API
   * and swaps the media gallery and product info.
   * @param {string} productUrl - The child product URL (e.g., "/products/child-handle")
   * @param {HTMLElement} button - The clicked button element
   * @private
   */
  async _fetchCombinedListingProduct(productUrl, button) {
    const section = this.productSection || document.querySelector('.js-product');
    if (!section) return;

    const sectionId = section.dataset.section;
    if (!sectionId) return;

    // Build Section Rendering API URL
    const optionValueId = button.dataset.optionValueId;
    let fetchUrl = `${productUrl}?section_id=${sectionId}`;
    if (optionValueId) {
      fetchUrl += `&option_values=${optionValueId}`;
    }

    // Cancel any pending fetch
    if (this._abortController) {
      this._abortController.abort();
    }
    this._abortController = new AbortController();

    try {
      const response = await fetch(fetchUrl, {
        signal: this._abortController.signal
      });

      if (!response.ok) return;

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Swap product title (Shopify Section Rendering API — same-origin trusted HTML)
      const currentTitle = section.querySelector('.product-title');
      const newTitle = doc.querySelector('.product-title');
      if (currentTitle && newTitle) {
        currentTitle.textContent = newTitle.textContent;
      }

      // Swap media gallery
      const currentMedia = section.querySelector('#product-media');
      const newMedia = doc.querySelector('#product-media');
      if (currentMedia && newMedia) {
        currentMedia.replaceChildren(...newMedia.childNodes);
      }

      // Update price
      const currentPrice = section.querySelector('.product-info__price');
      const newPrice = doc.querySelector('.product-info__price');
      if (currentPrice && newPrice) {
        currentPrice.replaceChildren(...newPrice.childNodes);
      }

      // Update SKU
      const currentSku = section.querySelector('.product-sku__value');
      const newSku = doc.querySelector('.product-sku__value');
      if (currentSku && newSku) {
        currentSku.textContent = newSku.textContent;
      }

      // Update description
      const currentDesc = section.querySelector('.product-description');
      const newDesc = doc.querySelector('.product-description');
      if (currentDesc && newDesc) {
        currentDesc.replaceChildren(...newDesc.childNodes);
      }

      // Update product URL on section element and browser URL
      section.dataset.productUrl = productUrl;
      window.history.replaceState({}, '', productUrl);

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Combined listing fetch error:', error);
      }
    }
  }

  // ============================================================
  // URL VARIANT PRESELECTION
  // ============================================================

  /**
   * Checks for and applies pre-selected variant from URL
   */
  checkPreselectedVariant() {
    const variantId = this._getURLVariantId();

    if (variantId) {
      // Try button selection first
      const button = this.container.querySelector(`[data-variant-id="${variantId}"]`);
      if (button && !button.disabled) {
        // Simulate click to trigger full selection flow
        button.click();
        return;
      }

      // Try dropdown selection
      if (this.variantDropdown) {
        const option = this.variantDropdown.querySelector(`option[value="${variantId}"]`);
        if (option) {
          this.variantDropdown.value = variantId;
          this.variantDropdown.dispatchEvent(new Event('change'));
          return;
        }
      }
    }

    // Auto-select first available variant when setting is enabled
    if (this.container.dataset.autoSelectFirst === 'true') {
      const firstAvailable = this.container.querySelector('[data-variant-button]:not([disabled])');
      if (firstAvailable) {
        firstAvailable.click();
        return;
      }
    }

    // No pre-selection, disable form submission with prominent CTA. The
    // server may have printed a number it cannot sell here (a taken one, or
    // an open one outside the release window), so the line says none is chosen.
    this.updateSelectedDisplay(null);
    this._disableAddToCartButton(`Select Your ${this.optionName}`);
  }

  // ============================================================
  // FORM VALIDATION OVERRIDE
  // ============================================================

  /**
   * Validates that a variant is selected
   * @returns {boolean}
   * @protected
   * @override
   */
  _validateSelection() {
    return !!this.selectedVariantId;
  }

  /**
   * Handles validation error with option-specific message
   * @protected
   * @override
   */
  _handleValidationError() {
    alert(`Please select a ${this.optionName.toLowerCase()} first`);
    this._focusFirstInput();
  }

  // ============================================================
  // PUBLIC API (Abstract method implementations)
  // ============================================================

  /**
   * Programmatically selects a variant by ID
   * @param {string|number} variantId - The variant ID to select
   * @returns {boolean} Whether the selection was successful
   * @override
   */
  selectVariant(variantId) {
    // Try button selection
    const button = this.container.querySelector(`[data-variant-id="${variantId}"]`);
    if (button && !button.disabled) {
      button.click();
      return true;
    }

    // Try dropdown selection
    if (this.variantDropdown) {
      const option = this.variantDropdown.querySelector(`option[value="${variantId}"]`);
      if (option) {
        this.variantDropdown.value = variantId;
        this.variantDropdown.dispatchEvent(new Event('change'));
        return true;
      }
    }

    return false;
  }

  /**
   * Gets the currently selected variant information
   * @returns {Object} Object containing variant id and value
   * @override
   */
  getSelectedVariant() {
    return {
      id: this.selectedVariantId,
      value: this.selectedValue,
      optionName: this.optionName
    };
  }

  /**
   * Resets the picker to initial state
   * @override
   */
  reset() {
    // Clear state
    this.selectedVariantId = null;
    this.selectedValue = null;

    // Reset button states
    this._resetAllButtonStates('[data-variant-button]');

    // Reset dropdown
    if (this.variantDropdown) {
      this.variantDropdown.value = '';
    }

    // Reset hidden input
    this._updateVariantIdInput(null);

    // Reset display
    this.updateSelectedDisplay(null);

    // Disable form submission
    this._disableAddToCartButton(`Select Your ${this.optionName}`);

    // Clear search inputs
    this._clearSearchInputs();

    // Hide inventory warning
    if (this.inventoryWarning) {
      this.inventoryWarning.hidden = true;
    }

    // Clear URL variant
    this._clearURLVariant();

    // Dispatch reset event
    this.container.dispatchEvent(new CustomEvent('variant-picker:reset', {
      bubbles: true,
      detail: {
        optionName: this.optionName
      }
    }));
  }

  // ============================================================
  // ADDITIONAL PUBLIC METHODS
  // ============================================================

  /**
   * Gets all available variant buttons
   * @returns {NodeList} All variant buttons
   */
  getVariantButtons() {
    return this.variantButtons;
  }

  /**
   * Gets all available (non-disabled) variants
   * @returns {Array} Array of available variant data
   */
  getAvailableVariants() {
    const variants = [];
    this.variantButtons.forEach(button => {
      if (!button.disabled) {
        variants.push({
          id: button.dataset.variantId,
          value: button.dataset.variantValue,
          originalValue: button.dataset.originalValue
        });
      }
    });
    return variants;
  }

  /**
   * Checks if a specific variant is available
   * @param {string|number} variantId - The variant ID to check
   * @returns {boolean} Whether the variant is available
   */
  isVariantAvailable(variantId) {
    const button = this.container.querySelector(`[data-variant-id="${variantId}"]`);
    return button && !button.disabled;
  }

  /**
   * Gets the picker type
   * @returns {string} The picker type (searchable-grid, grid, dropdown, buttons)
   */
  getPickerType() {
    return this.pickerType;
  }

  /**
   * Gets the option name
   * @returns {string} The option name (e.g., "Number", "Size")
   */
  getOptionName() {
    return this.optionName;
  }
}

// ============================================================
// AUTO-INITIALIZATION
// ============================================================

/**
 * Initializes all single option variant pickers on the page
 * @returns {Array} Array of SingleOptionVariantPicker instances
 */
function initSingleOptionVariantPickers() {
  const pickers = document.querySelectorAll('[data-single-option-picker]');
  const instances = [];

  pickers.forEach(picker => {
    // Skip if already initialized
    if (picker.singleOptionVariantPicker) return;

    const instance = new SingleOptionVariantPicker(picker);
    instances.push(instance);

    // Store instance on element for external access
    picker.singleOptionVariantPicker = instance;
  });

  // Store all instances globally for debugging
  window.SingleOptionVariantPickers = window.SingleOptionVariantPickers || [];
  window.SingleOptionVariantPickers.push(...instances);

  return instances;
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSingleOptionVariantPickers);
} else {
  initSingleOptionVariantPickers();
}

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.SingleOptionVariantPicker = SingleOptionVariantPicker;
  window.initSingleOptionVariantPickers = initSingleOptionVariantPickers;
}
